import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    toggleBtnSearch,
    disableBtnSearch,
    updateFocusedIds,
    updateFocusedInsideElement,
    setSearchHoveredColumn,
} from '../../redux/diagram/diagram.actions';
import {
    selectIsBtnSearchEnabled,
    selectScale,
    selectOriginXY,
} from '../../redux/diagram/diagram.selectors';
import { selectDiagramTitle } from '../../redux/main/main.selectors';

import { useElements, useMeta } from '../../lib/yjs';
import { parseQuery, buildSearchSource, runSearch, SPOTLIGHT_COMMANDS } from '../../lib/search';
import { computeElementViewport } from '../../utils/viewport/fit-viewport';
import { useViewportTween } from '../../hooks/useViewportTween';

import { SearchIcon } from '../Icon';
import { COMMAND_ICONS } from './commandIcons';

import './DiagramSearch.scss';

const DEBOUNCE_MS = 120;

// Temporary (user request 2026-07-27): typed queries hide command matches —
// data results only. Flip to true to show matched commands below the data.
const SHOW_COMMANDS_IN_TYPED_RESULTS = false;

// Preview/commit targets render this many screen px lower than dead center so
// the table is not covered by the spotlight panel anchored at the top.
const TARGET_SCREEN_OFFSET_Y = 200;

function offsetViewport(viewport) {
    return { ...viewport, y: viewport.y + TARGET_SCREEN_OFFSET_Y };
}

function Highlighted({ text, ranges }) {
    if (!ranges?.length) return <>{text}</>;
    const parts = [];
    let cursor = 0;
    ranges.forEach(([start, end], i) => {
        if (start > cursor) parts.push(<span key={`p${i}`}>{text.slice(cursor, start)}</span>);
        parts.push(<strong key={`h${i}`}>{text.slice(start, end)}</strong>);
        cursor = end;
    });
    if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
    return <>{parts}</>;
}

function resultKey(result) {
    if (result.kind === 'command') return `cmd-${result.commandId}`;
    if (result.kind === 'note') return `n-${result.elementId}`;
    return result.kind === 'table'
        ? `t-${result.elementId}`
        : `c-${result.elementId}-${result.columnId}`;
}

const SECTION_LABEL_KEYS = {
    tables: 'search_menu.section_tables',
    columns: 'search_menu.section_columns',
    canvas: 'search_menu.section_canvas',
    export: 'search_menu.section_export',
    preferences: 'search_menu.section_preferences',
    settings: 'search_menu.section_settings',
};

function sectionOf(row) {
    if (row.kind === 'table') return 'tables';
    if (row.kind === 'column') return 'columns';
    return row.section;
}

export const DiagramSearch = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { diagramId } = useParams();

    const isOpen = useSelector(selectIsBtnSearchEnabled);
    const scale = useSelector(selectScale);
    const originXY = useSelector(selectOriginXY);
    const diagramTitle = useSelector(selectDiagramTitle);

    const elements = useElements();
    const meta = useMeta();

    const { tweenTo, cancelTween } = useViewportTween();

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);

    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Viewport as of search-open — the preview baseline and the cancel-restore
    // target (Task 7). committedRef distinguishes commit-close from cancel-close.
    const capturedViewportRef = useRef(null);
    const committedRef = useRef(false);
    const hasPreviewedRef = useRef(false);
    const activeKeyRef = useRef(null);
    const previewSelectionKeyRef = useRef(null); // last row whose element we hover-focused

    // Live viewport copy so the open/close effect does not re-run on pan/zoom.
    const viewportRef = useRef({ scale, originXY });
    viewportRef.current = { scale, originXY };

    // Reads the live viewport in {scale, x, y} form (tweenTo's input shape).
    const liveViewport = useCallback(() => ({
        scale: viewportRef.current.scale,
        x: viewportRef.current.originXY.x,
        y: viewportRef.current.originXY.y,
    }), []);

    // All user-driven active-row changes go through here so the active row is
    // tracked by identity, not just list position — collaborative edits can
    // shift positions under the cursor.
    const setActive = useCallback((index, rows) => {
        const row = index >= 0 ? rows[index] : null;
        activeKeyRef.current = row ? resultKey(row) : null;
        setActiveIndex(index);
    }, []);

    // Open: capture viewport, reset state, focus the input.
    // Close without commit (Esc / outside click / toolbar toggle): restore it.
    useEffect(() => {
        if (isOpen) {
            cancelTween(); // a leftover commit/restore tween must not fight the fresh baseline capture
            capturedViewportRef.current = {
                scale: viewportRef.current.scale,
                x: viewportRef.current.originXY.x,
                y: viewportRef.current.originXY.y,
            };
            committedRef.current = false;
            hasPreviewedRef.current = false;
            activeKeyRef.current = null;
            previewSelectionKeyRef.current = null;
            setQuery('');
            setDebouncedQuery('');
            if (debouncedQueryRef.current === '') {
                // the '' reset below won't re-fire the query-settle effect, so
                // activate the first command directly (commands have no bbox —
                // no viewport jump on open)
                setActive(0, rowsRef.current);
            } else {
                // stale query from the previous session: rows are stale too —
                // wait for the reset to settle; the query effect activates row 0
                setActiveIndex(-1);
            }
            const timer = setTimeout(() => inputRef.current?.focus(), 0);
            return () => clearTimeout(timer);
        }
        if (capturedViewportRef.current && !committedRef.current && hasPreviewedRef.current) {
            tweenTo(liveViewport(), capturedViewportRef.current);
        }
        capturedViewportRef.current = null;
        hasPreviewedRef.current = false;
        return undefined;
    }, [isOpen, tweenTo, liveViewport, cancelTween, setActive]);

    // Debounce keystrokes into debouncedQuery.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    const results = useMemo(() => {
        if (!isOpen) return [];
        const source = buildSearchSource({ viewId: diagramId, viewTitle: diagramTitle, elements });
        return runSearch([source], parseQuery(debouncedQuery));
    }, [isOpen, elements, debouncedQuery, diagramId, diagramTitle]);

    const commandItems = useMemo(() => SPOTLIGHT_COMMANDS.map((command) => ({
        kind: 'command',
        commandId: command.id,
        section: command.section,
        label: t(command.labelKey),
        keywords: command.keywords,
    })), [t]);

    const rows = useMemo(() => {
        const { terms, filters } = parseQuery(debouncedQuery);
        const allTerms = [...terms, ...filters.map((f) => f.raw)];
        if (!allTerms.length) return isOpen ? commandItems : [];
        // Temporary (2026-07-27): typed queries show data results only — flip
        // SHOW_COMMANDS_IN_TYPED_RESULTS to bring matched commands back below.
        if (!SHOW_COMMANDS_IN_TYPED_RESULTS) return results;
        const commandRows = commandItems.filter((item) => allTerms.every((term) =>
            item.label.toLowerCase().includes(term) ||
            item.keywords.some((keyword) => keyword.includes(term))
        ));
        return [...results, ...commandRows];
    }, [isOpen, results, commandItems, debouncedQuery]);

    // Latest-refs so the open effect can read these without re-running on
    // every keystroke (re-running it would re-capture the viewport mid-typing).
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const debouncedQueryRef = useRef(debouncedQuery);
    debouncedQueryRef.current = debouncedQuery;

    // A collaborator's edit can reshuffle the list: re-point activeIndex at
    // the same logical row, or drop the active state if the row vanished.
    useEffect(() => {
        if (activeKeyRef.current == null) return;
        const index = rows.findIndex((result) => resultKey(result) === activeKeyRef.current);
        if (index === -1) activeKeyRef.current = null;
        setActiveIndex(index);
    }, [rows]);

    // Query settled -> first item starts active (also lands here on the
    // open-reset when the previous session left a query). A data first-row
    // auto-previews by design; a command first-row is highlight-only.
    useEffect(() => {
        if (rowsRef.current.length) {
            setActive(0, rowsRef.current);
        } else {
            activeKeyRef.current = null;
            setActiveIndex(-1);
        }
    }, [debouncedQuery, setActive]);

    const commitResult = useCallback((result) => {
        if (result.kind === 'command') {
            committedRef.current = true;
            if (hasPreviewedRef.current && capturedViewportRef.current) {
                tweenTo(liveViewport(), capturedViewportRef.current);
            }
            dispatch(toggleBtnSearch());
            const command = SPOTLIGHT_COMMANDS.find((c) => c.id === result.commandId);
            if (command) {
                setTimeout(() => {
                    // a still-running restore tween would overwrite viewport
                    // commands (zoom-to-fit) with its remaining frames
                    cancelTween();
                    command.run(dispatch, { diagramId, dbEngine: meta.dbEngine });
                }, 100);
            }
            return;
        }
        if (!elements[result.elementId]) return; // deleted mid-flight — the list refreshes
        committedRef.current = true;
        // TOGGLE_BTN_SEARCH clears the selection, so close first, select after;
        // the tween's APPLY_VIEWPORT frames leave the selection intact.
        dispatch(toggleBtnSearch());
        dispatch(updateFocusedIds([result.elementId]));
        if (result.kind === 'column') {
            dispatch(updateFocusedInsideElement(result.elementId, result.columnId, result.columnIndex, 'column'));
        }
        const baseScale = capturedViewportRef.current?.scale ?? viewportRef.current.scale;
        const viewport = computeElementViewport(result.bbox, baseScale, window.innerWidth, window.innerHeight);
        tweenTo(liveViewport(), offsetViewport(viewport));
    }, [dispatch, elements, tweenTo, liveViewport, cancelTween, diagramId, meta]);

    // Preview: the active row temporarily moves the viewport AND focuses the
    // element on canvas (hover-focus). Leaving the active state (activeIndex
    // -1, e.g. the query changed) snaps both back so typing continues from
    // where the user started.
    useEffect(() => {
        if (!isOpen) return;
        const result = activeIndex >= 0 ? rows[activeIndex] : null;
        if (result && result.bbox && resultKey(result) === activeKeyRef.current) {
            hasPreviewedRef.current = true;
            const baseScale = capturedViewportRef.current?.scale ?? viewportRef.current.scale;
            const viewport = computeElementViewport(result.bbox, baseScale, window.innerWidth, window.innerHeight);
            tweenTo(liveViewport(), offsetViewport(viewport));
            // Focus follows the preview — table-level only: focusing the column
            // (updateFocusedInsideElement) opens its edit UI, which must stay
            // click-only. The key guard stops value-identical re-dispatches
            // when collab edits refresh `rows` under the cursor.
            if (previewSelectionKeyRef.current !== resultKey(result)) {
                previewSelectionKeyRef.current = resultKey(result);
                dispatch(updateFocusedIds([result.elementId]));
                dispatch(setSearchHoveredColumn(
                    result.kind === 'column'
                        ? { elementId: result.elementId, columnId: result.columnId }
                        : null
                ));
            }
        } else if ((!result || !result.bbox) && hasPreviewedRef.current && capturedViewportRef.current) {
            // no active row, or the active row is a command (no bbox): a prior
            // data preview must not linger — snap viewport, focus, and column
            // highlight back
            hasPreviewedRef.current = false;
            previewSelectionKeyRef.current = null;
            dispatch(updateFocusedIds([]));
            dispatch(setSearchHoveredColumn(null));
            tweenTo(liveViewport(), capturedViewportRef.current);
        }
        // result present but identity mismatch: the list shifted under the
        // cursor — the reconciliation effect re-points activeIndex next pass.
    }, [isOpen, activeIndex, rows, tweenTo, liveViewport, dispatch]);

    // Outside click cancels. Clicks on the toolbar search button are ignored —
    // its own onClick runs the toggle (otherwise the two would double-toggle).
    useEffect(() => {
        if (!isOpen) return;
        const handleDocClick = (event) => {
            if (event.button !== 0) return;
            if (rootRef.current?.contains(event.target)) return;
            if (event.target.closest?.('.control-search')) return;
            dispatch(disableBtnSearch());
        };
        document.addEventListener('click', handleDocClick);
        return () => document.removeEventListener('click', handleDocClick);
    }, [isOpen, dispatch]);

    // Ctrl/⌘+F or Ctrl/⌘+K while open: return focus to the input (the
    // ControlsTopLeft handler swallows the browser default; stopPropagation
    // there does not stop other document-level listeners, so this still runs).
    useEffect(() => {
        if (!isOpen) return;
        const handleDocKeyDown = (event) => {
            const isCtrl = event.metaKey || event.ctrlKey;
            if (isCtrl && (event.code === 'KeyF' || event.code === 'KeyK') && !event.altKey && !event.shiftKey) {
                event.preventDefault();
                inputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleDocKeyDown);
        return () => document.removeEventListener('keydown', handleDocKeyDown);
    }, [isOpen]);

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!rows.length) return;
            // wrap: past the last row -> back to the first
            setActive(activeIndex + 1 > rows.length - 1 ? 0 : activeIndex + 1, rows);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!rows.length) return;
            // wrap: before the first row -> the last one
            setActive(activeIndex - 1 < 0 ? rows.length - 1 : activeIndex - 1, rows);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const result = rows[activeIndex] ?? rows[0];
            if (result) commitResult(result);
        }
        // Escape is handled by the document-level handler (clickEscape closes search).
    };

    // Keep the active row visible while arrowing through a long list.
    useEffect(() => {
        if (activeIndex < 0) return;
        listRef.current
            ?.querySelector('.diagram-search-row.active')
            ?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    if (!isOpen) return null;

    const trimmedQuery = debouncedQuery.trim();

    return (
        <div className="diagram-search" ref={rootRef}>
            <div className="diagram-search-input-row">
                {/* <SearchIcon /> */}
                <input
                    ref={inputRef}
                    className="diagram-search-input"
                    type="text"
                    value={query}
                    placeholder={t('search_menu.placeholder')}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    spellCheck={false}
                />
            </div>

            {rows.length === 0 && trimmedQuery === '' ? (
                <div className="diagram-search-hint">{t('search_menu.hint')}</div>
            ) : rows.length === 0 ? (
                <ul
                    className="diagram-search-results"
                    role="listbox"
                >
                    <li className="diagram-search-section" role="presentation">{t('search_menu.quick_results')} "{query}"</li>
                    <li className="diagram-search-empty">{t('search_menu.no_results')}</li>
                </ul>
            ) : (
                <ul
                    className="diagram-search-results"
                    role="listbox"
                    ref={listRef}
                    onMouseLeave={() => setActive(-1, rows)}
                >
                    {/* typing with no data matches: keep the quick-results
                        section visible with its empty state — matched command
                        sections continue below */}
                    {trimmedQuery !== '' && results.length === 0 ? (
                        <>
                            <li className="diagram-search-section" role="presentation">{t('search_menu.quick_results')} "{query}"</li>
                            {/* <li className="diagram-search-empty">{t('search_menu.no_results')}</li> */}
                        </>
                    ) : null}
                    {rows.map((result, index) => {
                        const CommandIcon = result.kind === 'command' ? COMMAND_ICONS[result.commandId] : null;
                        return (
                            <React.Fragment key={resultKey(result)}>
                                {(() => {
                                    const isData = result.kind === 'table' || result.kind === 'column' || result.kind === 'note';
                                    const prev = index > 0 ? rows[index - 1] : null;
                                    const prevIsData = prev && (prev.kind === 'table' || prev.kind === 'column' || prev.kind === 'note');
                                    if (isData) {
                                        return (index === 0 || !prevIsData) ? (
                                            <li className="diagram-search-section" role="presentation">
                                                {t('search_menu.quick_results')} "{query}"
                                            </li>
                                        ) : null;
                                    }
                                    return (index === 0 || prevIsData || sectionOf(prev) !== sectionOf(result)) ? (
                                        <li className="diagram-search-section" role="presentation">
                                            {t(SECTION_LABEL_KEYS[sectionOf(result)])}
                                        </li>
                                    ) : null;
                                })()}

                                <li
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={`diagram-search-row ${result.kind} ${index === activeIndex ? 'active' : ''}`}
                                    onMouseEnter={() => setActive(index, rows)}
                                    onClick={() => commitResult(result)}
                                >
                                    {result.kind === 'command' ? (
                                        <>
                                            {CommandIcon ? <CommandIcon /> : null}
                                            <span className="row-name">{result.label}</span>
                                            {/* <span className="row-type">{t('search_menu.command_tag')}</span> */}
                                        </>
                                    ) : result.kind === 'table' ? (
                                        <>
                                            <svg className="search-icon-table" fill="rgb(134, 145, 148)" stroke="rgb(134, 145, 148)" strokeWidth="0.7px" width="20px" height="21px" viewBox="0 -1 24 24" style={{ marginTop: '0px', marginRight: '2px', marginLeft: '2px' }}><g strokeLinecap="round" strokeLinejoin="round"></g><g> <g> <path d="M18.44,3.06H5.56a2.507,2.507,0,0,0-2.5,2.5V18.44a2.507,2.507,0,0,0,2.5,2.5H18.44a2.514,2.514,0,0,0,2.5-2.5V5.56A2.514,2.514,0,0,0,18.44,3.06ZM8.71,19.94H5.56a1.5,1.5,0,0,1-1.5-1.5V15.33H8.71Zm0-5.61H4.06V9.67H8.71Zm0-5.66H4.06V5.56a1.5,1.5,0,0,1,1.5-1.5H8.71Zm11.23,9.77a1.511,1.511,0,0,1-1.5,1.5H9.71V15.33H19.94Zm0-4.11H9.71V9.67H19.94Zm0-5.66H9.71V4.06h8.73a1.511,1.511,0,0,1,1.5,1.5Z"></path> </g> </g></svg>
                                            <span className="row-name">
                                                <Highlighted
                                                    text={result.tableName || t('search_menu.untitled')}
                                                    ranges={result.tableName ? result.nameRanges : []}
                                                />
                                            </span>
                                            <span className="row-type">
                                                <Highlighted text={t('search_menu.type_table')} ranges={''} />
                                            </span>
                                        </>
                                    ) : result.kind === 'note' ? (
                                        <>
                                            <svg className="search-icon-note" width="20px" height="20px" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '2px', marginRight: '2px', minWidth: '20px' }}>
                                                <path d="M5 4h14a1 1 0 0 1 1 1v9l-6 6H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" stroke="rgb(134, 145, 148)" strokeWidth="1.9" strokeLinejoin="round"></path>
                                                <path d="M14 20v-5a1 1 0 0 1 1-1h5" stroke="rgb(134, 145, 148)" strokeWidth="1.9" strokeLinejoin="round"></path>
                                            </svg>
                                            <span className="row-name">
                                                <Highlighted text={result.noteText || t('search_menu.untitled')} ranges={result.noteText ? result.textRanges : []} />
                                            </span>
                                            <span className="row-type">
                                                <Highlighted text={t('search_menu.type_note')} ranges={''} />
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className='search-icon-column' width="21px" height="21px" viewBox="0 -1 24 24" fill="none" style={{ marginLeft: '1px', marginRight: '2px' }}>
                                                <g>
                                                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="rgb(134, 145, 148)" strokeWidth="1.9px"></rect>
                                                    <path d="M16 10L8 10" stroke="rgb(134, 145, 148)" strokeWidth="1.9px" strokeLinecap="round"></path>
                                                    <path d="M16 14L8 14" stroke="rgb(134, 145, 148)" strokeWidth="1.9px" strokeLinecap="round"></path>
                                                </g>
                                            </svg>
                                            <span className="row-name">
                                                <Highlighted text={result.columnName} ranges={result.nameRanges} />
                                            </span>
                                            {result.columnType ? (
                                                <span className="row-type">
                                                    <Highlighted text={result.columnType} ranges={result.typeRanges} />
                                                </span>
                                            ) : null}
                                            <span className="row-table-hint">{result.tableName || t('search_menu.untitled')}</span>
                                            {/* <span className="row-badges">
                                        {result.flags.isPK ? <span className="badge pk">PK</span> : null}
                                        {result.flags.isFK ? <span className="badge fk">FK</span> : null}
                                        {result.flags.isNotNull ? <span className="badge nn">NN</span> : null}
                                    </span> */}
                                        </>
                                    )}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default DiagramSearch;
